import { z } from "zod";

// ============================================
// Enums (matching backend)
// ============================================

export enum AccountStatus {
  ACTIVE = 'ACTIVE',
  FIRST_LOGIN_PENDING = 'FIRST_LOGIN_PENDING',
  CREATE_PROFILE_PENDING = 'CREATE_PROFILE_PENDING',
  RESET_PASSWORD_REQUESTED = 'RESET_PASSWORD_REQUESTED',
  DELETED = 'DELETED',
  BLOCKED = 'BLOCKED',
}

/** Statuses that require user to complete /reset-password (set password or create profile) before accessing other routes */
export const MUST_COMPLETE_RESET_STATUSES: AccountStatus[] = [
  AccountStatus.FIRST_LOGIN_PENDING,
  AccountStatus.CREATE_PROFILE_PENDING,
  AccountStatus.RESET_PASSWORD_REQUESTED,
];

/** Statuses that invalidate session – clear auth and redirect to login */
export const INVALID_SESSION_STATUSES: AccountStatus[] = [
  AccountStatus.BLOCKED,
  AccountStatus.DELETED,
];

export enum OtpPurpose {
  EMAIL_VERIFICATION_SIGN_UP = 'EMAIL_VERIFICATION_SIGN_UP',
  EMAIL_VERIFICATION_RESET_PWD = 'EMAIL_VERIFICATION_RESET_PWD',
}

// ============================================
// Request DTOs (aligned with backend)
// ============================================

/** Request for saving temporary user during signup */
export interface SaveTempUserRequest {
  email: string;
  mobile: string;
  surName: string;
  firstName: string;
  middleName?: string; // optional; send when user provides it
  lastName: string;
  dob: string; // YYYY-MM-DD format
  passportTypeId: number;
  passportIssueCountryId: number;
  passportNumber: string;
  passportIssueDate: string; // YYYY-MM-DD format
  passportExpiryDate: string; // YYYY-MM-DD format
  passportIssuePlace: string;
  isDataProtectionInfoAgreed: boolean;
}

/** Request for sending OTP */
export interface SendOtpRequest {
  email: string;
  purpose: OtpPurpose;
}

/** Request for verifying OTP */
export interface VerifyOtpRequest {
  email: string;
  otp: string;
  purpose: OtpPurpose;
}

/** Request for creating user from temp data */
export interface CreateUserRequest {
  email: string;
}

/** Request for initiating forgot password flow */
export interface ForgotPasswordRequest {
  email: string;
}

/** Request for resetting password (after forgot password) */
export interface ResetPasswordRequest {
  password: string;
}

/** Request for updating password (first login or change password) */
export interface UpdatePasswordRequest {
  password: string; // current/temp password
  newPassword: string;
}

/** Request for updating user profile */
export interface UpdateUserRequest {
  email: string;
  mobile: string;
  surName: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  dob: string; // YYYY-MM-DD
  passportTypeId: number;
  passportIssueCountryId: number;
  passportNumber: string;
  passportIssueDate: string; // YYYY-MM-DD
  passportExpiryDate: string; // YYYY-MM-DD
  passportIssuePlace: string;
  isDataProtectionInfoAgreed: boolean;
  locationId?: number;
  locationType?: string;
  visaTypeId?: number;
  visaSubTypeId?: number;
}

/** Response from update user */
export interface UpdateUserResponse {
  user: User;
  applicantDetails: Record<string, any>;
}

/** Request for recovering user ID */
export interface RecoverUserIdRequest {
  passportNumber: string;
  dateOfBirth: string; // YYYY-MM-DD
  contact: string;
  passportExpiryDate: string; // YYYY-MM-DD
}

// ============================================
// Response Types
// ============================================

/** Response from save temp user */
export interface TempUserResponse {
  id: number;
  email: string;
  mobile: string;
  surName: string;
  firstName: string;
  lastName: string;
  dob: string;
  passportTypeId: number;
  passportIssueCountryId: number;
  passportNumber: string;
  passportIssueDate: string;
  passportExpiryDate: string;
  passportIssuePlace: string;
  isDataProtectionInfoAgreed: boolean;
  isEmailOtpVerified: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Response from OTP operations */
export interface OtpResponse {
  message: string;
}

/** Response from forgot password */
export interface ForgotPasswordResponse {
  sessionId: string;
  expiresAt: string;
}

/** Response from create user */
export interface CreateUserResponse {
  id: number;
  email: string;
  status: AccountStatus;
}

/** Response from recover user ID */
export interface RecoverUserIdResponse {
  success: boolean;
  message: string;
}

// ============================================
// User & Session Types
// ============================================

export interface User {
  id: number;
  email: string;
  mobile: string | null;
  surName: string | null;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  dob: string | Date | null;
  passportTypeId: number | null;
  passportIssueCountryId: number | null;
  passportNumber: string | null;
  passportIssueDate: string | Date | null;
  passportExpiryDate: string | Date | null;
  passportIssuePlace: string | null;
  role: string;
  status: AccountStatus;
  languagePreference: string | null;
  isMobileOtpVerified: boolean;
  isEmailOtpVerified: boolean;
  createdAt: Date;
  updatedAt: Date;
  isActive: boolean;
  session?: SessionMetadata | null;
}

export interface SessionMetadata {
  idleTimeoutMinutes: number;
  absoluteExpiresAt: string;
  lastAccessedAt: string;
}

/** Raw response shape from GET /users/me when backend returns user + sessionMetadata at top level */
export interface MeApiResponse {
  user: Omit<User, 'session'>;
  sessionMetadata: SessionMetadata;
}

export interface RefreshSessionResponse {
  message: string;
  expiresAt: string;
  lastAccessedAt: string;
  /** When present, merge into user.session for accurate absolute timeout */
  absoluteExpiresAt?: string;
}

export interface LoginResponse {
  id: number;
  status: AccountStatus;
}

export interface AuthResponse {
  user: User | null;
  forceChangePassword?: boolean;
}

// ============================================
// Validation Schemas
// ============================================

export const loginSchema = z.object({
  email: z.string().trim().email("Invalid email address"),
  password: z.string().trim().min(1, "Password is required"),
});

export type LoginCredentials = z.infer<typeof loginSchema>;

